import PrepareToSigning from './prepareToSigningPdfJs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDocumentState, DocumentProvider } from '../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../utils/document-wizard-utils/document-state-from-response';
import { useDocumentWizardErrorNotifier } from '../document-wizard/shared/hooks/use-document-wizard-error-notifier/use-document-wizard-error-notifier.hook';
import { useDeal } from '../../contexts/DealContext';
import { useDocumentTeamPermissions } from '../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { useDocumentRole } from '../../shared/hooks/useDocumentRole/useDocumentRole';
import { fillPlaceholders, paths } from '../../routing';
import LoadingSpinner from '../../components/loading-spinner';
import { useIntl as reactIntlUseIntl } from 'react-intl';
import { ApolloClient, useApolloClient as apolloUseApolloClient, useMutation } from '@apollo/client';
import { graphqlErrorsToUiErrors } from '../../api';
import ErrorMessage from '../../components/error-message';
import { getDocumentById, getDocumentByIdOfferQuery } from '../../utils/api/get-document-by-id';
import { DocumentPayload } from '../../utils/api/document-interface.types';
import { mutations, queriesV2 } from '../../api';
import { DocumentEditorErrors, DocumentSourceEnum } from '../../components/document-wizard/document-editor/constants/EditorConstats';
import { useAuth } from '../../auth';
import { useNotifications } from '../../notifications';
import { DocumentStatusEnum } from '../../shared/constants/document';
import { regenerateDocumentPdf } from '../../api/mutations';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import MobileSigningPrepWarningModal from '../../components/document-wizard/document-wizard-modal/custom-modals/mobileSigningPrepWarningModal/MobileSigningPrepWarningModal';
// Removed unused imports: mergeAndUploadXfdf, needsClientSideMerge

type PrepareToSigningContainerProps = {
  useApolloClient?: (...args: any[]) => any;
  useIntl?: (...args: any[]) => any;
  getDocument?: (documentId: string | number) => (client: ApolloClient<object>) => Promise<DocumentPayload>;
};


const PrepareToSigningContainerInner = ({
  useIntl = reactIntlUseIntl,
  useApolloClient = apolloUseApolloClient,
  getDocument = getDocumentById,
}: PrepareToSigningContainerProps) => {
  const apolloClient = useApolloClient();
  const reactIntl = useIntl();
  const navigate = useNavigate();
  const { documentId, teamId, dealId } = useParams();
  const { state, updateDocumentState, resetDocumentState } = useDocumentState();
  const { setCurrentDeal } = useDeal();
  const [documentGraphqlError, setDocumentGraphqlError] = useState(null);
  const document = state.document;
  const documentTitle = state.title;
  const lastUpdated = state.lastUpdated;
  const documentStatus = state.status;
  const documentContentType = !state.source ? null : state.source === DocumentSourceEnum.APP ? 'application/pdf' : state.contentType;
  const documentSource = state.source;
  const [, { hide: hideNotifications, success: notifySuccess }] = useNotifications();
  const notifyError = useDocumentWizardErrorNotifier();
  const [{ authLoading, user }] = useAuth();
  const { isDocumentCreator, hasCurrentTeamPossession, isDocumentRoleLoading } = useDocumentRole(documentId!);
  const generatedDocumentPdf = state.generatedDocumentPdfUrl;
  const isDocumentExecuted = [DocumentStatusEnum.EXECUTED].includes(documentStatus as DocumentStatusEnum);

  const pdfUrl = generatedDocumentPdf;
  const [pdfLoadTimeout, setPdfLoadTimeout] = useState(false);
  const regenerationTriggered = useRef(false);
  const pollAttempts = useRef(0);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [mobileWarningDismissed, setMobileWarningDismissed] = useState(false);


  const [triggerPdfRegeneration] = useMutation(regenerateDocumentPdf, {
    onCompleted: ({ regenerateDocumentPdf: result }) => {
      notifySuccess({
        message: 'PDF regeneration started. This may take a moment.',
        pinned: false,
      });
      regenerationTriggered.current = true;
    },
    onError: (error) => {
      console.error('Failed to trigger PDF regeneration:', error);
      notifyError({
        message: 'Failed to regenerate PDF. Please try refreshing the page.',
        pinned: false,
      });
    },
  });

  const isDataLoading = isDocumentRoleLoading || authLoading;

  // Show mobile warning when on mobile and not dismissed
  useEffect(() => {
    if (isMobile && !mobileWarningDismissed && !isDataLoading && documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN) {
      setShowMobileWarning(true);
    }
  }, [isMobile, mobileWarningDismissed, isDataLoading, documentStatus]);

  const handleError = useCallback(() => {
    notifyError({
      message: reactIntl.formatMessage({
        id: 'lease-document-wizard.fetch-error',
        defaultMessage: 'Unable to fetch the Document',
      }),
      pinned: false,
    });
  }, [notifyError, reactIntl]);

  useEffect(() => {
    if (
      !isDataLoading &&
      documentStatus &&
      teamId &&
      dealId &&
      documentId &&
      documentStatus !== DocumentStatusEnum.PREPARING_TO_SIGN
    ) {
      const urlToNavigate = fillPlaceholders(paths.Documents.document, { teamId, dealId, documentId });

      navigate(urlToNavigate);
    }
  }, [dealId, documentStatus, isDataLoading, navigate, documentId, teamId]);

  useEffect(() => {
    return () => {
      hideNotifications();
    };
  }, [hideNotifications]);

  useEffect(() => {
    if (!documentId) {
      return;
    }

    // Force fresh fetch to get newly generated PDF URL (critical for CKEditor documents)
    // Using network-only to bypass Apollo cache and get the latest PDF after generation
    apolloClient
      .query({
        query: getDocumentByIdOfferQuery,
        variables: { documentId },
        fetchPolicy: 'network-only',
      })
      .then(async ({ data }) => {
        const response = data.getDocumentById;

        // PDF.js handles all PDF rendering - form fields and annotations stored in JSONB columns
        setCurrentDeal(response.deal);

        // @ts-ignore
        updateDocumentState(documentStateFromResponse(response));
      })
      .catch((err) => {
        console.error('Error loading document:', err);
        setDocumentGraphqlError(err);
        handleError();
      });
  }, [apolloClient, updateDocumentState, getDocument, handleError, documentId]); // Removed 'document' to prevent infinite loop

  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });
  const onClosePath = paths.deals.detail;
  const onCloseParams: { [key: string]: string } | {} = document && teamId ? { teamId, dealId } : {};
  const onCloseUrl = document && teamId ? fillPlaceholders(onClosePath, onCloseParams) : '';

  const handleLeaseWizardClose = useCallback(() => {
    navigate(onCloseUrl);
    resetDocumentState();
  }, [resetDocumentState, navigate, onCloseUrl]);

  const handleMobileWarningGoBack = useCallback(() => {
    setShowMobileWarning(false);
    navigate(onCloseUrl);
    resetDocumentState();
  }, [resetDocumentState, navigate, onCloseUrl]);

  const handleMobileWarningContinue = useCallback(() => {
    setShowMobileWarning(false);
    setMobileWarningDismissed(true);
  }, []);

  const isLockedRef = useRef<boolean>(false);

  const [isDocumentLocked, setIsDocumentLocked] = useState(false);
  const [lockDocument, { loading: isLockLeaseDocumentLoading }] = useMutation(mutations.lockDocument);

  const [unlockDocument] = useMutation(mutations.unlockDocument);
  useEffect(() => {
    lockDocument({ variables: { documentId: documentId!, userId: user.id } })
      .then(() => {
        isLockedRef.current = true;
      })
      .catch((err: any) => {
        if (hasCurrentTeamPossession) {
          setIsDocumentLocked(true);
          !isDocumentExecuted && notifyError({ message: DocumentEditorErrors.IS_LOCKED.defaultMessage, pinned: true });
        }
      });

    return () => {
      if (isLockedRef.current) {
        unlockDocument({
          variables: { documentId: documentId!, userId: user.id, sessionActivities: [] },
          refetchQueries: [
            {
              query: queriesV2.getDocumentsByDeal,
              variables: {
                dealId,
              },
            },
          ],
        });
      }
    };
  }, []); // eslint-disable-line

  const handleUnlock = useCallback(() => {
    if (isLockedRef.current) {
      unlockDocument({ variables: { documentId: documentId!, userId: user.id, sessionActivities: [] } });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    window.addEventListener('beforeunload', handleUnlock);

    return () => {
      window.removeEventListener('beforeunload', handleUnlock);
    };
  }, []); // eslint-disable-line

  // Set a timeout for PDF loading - if it takes more than 30 seconds, show retry option
  // Also poll for PDF availability every 3 seconds
  useEffect(() => {
    if (!pdfUrl && documentStatus === 'preparing_to_sign') {
      pollAttempts.current = 0;
      regenerationTriggered.current = false;

      const timeoutId = setTimeout(() => {
        setPdfLoadTimeout(true);
      }, 30000);

      pollInterval.current = setInterval(() => {
        pollAttempts.current += 1;
        
        apolloClient
          .query({
            query: getDocumentByIdOfferQuery,
            variables: { documentId },
            fetchPolicy: 'network-only',
          })
          .then(({ data }) => {
            const response = data.getDocumentById;
            if (response.generatedDocumentPdfUrl) {
              // PDF is ready, update the store and stop polling
              setCurrentDeal(response.deal);
              // @ts-ignore
              updateDocumentState(documentStateFromResponse(response));

              // Clear the polling interval since PDF is now available
              if (pollInterval.current) {
                clearInterval(pollInterval.current);
                pollInterval.current = null;
              }
              
              pollAttempts.current = 0;
            } else if (pollAttempts.current >= 5 && !regenerationTriggered.current) {
              // After 15 seconds (5 attempts * 3 seconds), trigger regeneration
              console.log('PDF not available after polling, triggering regeneration...');
              triggerPdfRegeneration({
                variables: { documentId: documentId! }
              });
            }
          })
          .catch((_err) => {
            // Silently ignore errors during polling
          });
      }, 3000); // Poll every 3 seconds

      return () => {
        clearTimeout(timeoutId);
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
        }
      };
    }
  }, [pdfUrl, documentStatus, documentId, apolloClient, updateDocumentState, triggerPdfRegeneration]);

  // In prepare-to-sign mode, the document cannot be edited (only signing fields can be added)
  // The PDF won't change unless the user goes back to edit mode via "Edit Document" button
  // If user clicks "Edit Document", they'll navigate away from this page and return with a fresh PDF

  // Handle GraphQL errors (not found, permission denied) - redirect to deal page
  useEffect(() => {
    if (!document && documentGraphqlError && teamId && dealId) {
      const dealUrl = fillPlaceholders(paths.deals.detail, { teamId, dealId });
      navigate(dealUrl, { replace: true });
    }
  }, [document, documentGraphqlError, teamId, dealId, navigate]);

  if (!document && documentGraphqlError) {
    if (teamId && dealId) {
      return <LoadingSpinner />;
    }
    const errors = graphqlErrorsToUiErrors(documentGraphqlError);
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  // Check for critical data that must be present
  if (
    !document ||
    !documentId ||
    !dealId ||
    !teamId ||
    isDataLoading ||
    !documentStatus ||
    isLockLeaseDocumentLoading
  ) {
    return <LoadingSpinner />;
  }

  const isImportedDocument = documentSource === 'imported';
  if (isImportedDocument && (!documentSource || !documentContentType)) {
    return <LoadingSpinner />;
  }

  if (!pdfUrl) {
    if (pdfLoadTimeout) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: '20px',
            padding: '20px',
          }}
        >
          <div style={{ color: '#666', fontSize: '16px', textAlign: 'center' }}>
            Document preparation is taking longer than expected.
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#006c90',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Retry
            </button>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return <LoadingSpinner />;
  }

  return (
    <>
      <MobileSigningPrepWarningModal
        show={showMobileWarning}
        onGoBack={handleMobileWarningGoBack}
        onContinue={handleMobileWarningContinue}
      />
      <PrepareToSigning
      lastUpdated={lastUpdated}
      documentTitle={documentTitle || ''}
      isDocumentCreator={isDocumentCreator}
      documentId={documentId}
      dealId={dealId}
      teamId={teamId}
      permissions={permissions}
      handleClose={handleLeaseWizardClose}
      pdfUrl={pdfUrl}
      isDocumentLocked={isDocumentLocked}
      documentContentType={documentContentType}
      documentStatus={documentStatus}
      documentSource={documentSource}
    />
    </>
  );
};

const PrepareToSigningContainer = (props: PrepareToSigningContainerProps) => (
  <DocumentProvider>
    <PrepareToSigningContainerInner {...props} />
  </DocumentProvider>
);

export default PrepareToSigningContainer;
